import { useState, useEffect, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { KeycloakUserInfo } from '../types';

const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'https://oauth.dotnethub.pl',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'DotnetHub',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'dotnethub-web',
};

interface UseKeycloakReturn {
  keycloak: Keycloak | null;
  authenticated: boolean;
  loading: boolean;
  userInfo: KeycloakUserInfo | null;
  logout: () => void;
}

export const useKeycloak = (): UseKeycloakReturn => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [userInfo, setUserInfo] = useState<KeycloakUserInfo | null>(null);
  const isInitialized = useRef<boolean>(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }
    
    isInitialized.current = true;
    console.log('Initializing Keycloak with config:', keycloakConfig);
    
    const kc = new Keycloak(keycloakConfig);

    kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    })
      .then((auth) => {
        console.log('Keycloak initialized, authenticated:', auth);
        
        if (auth) {
          setAuthenticated(true);
          setKeycloak(kc);
          
          // Load user info
          kc.loadUserInfo()
            .then((info) => {
              console.log('User info loaded:', info);
              setUserInfo(info as KeycloakUserInfo);
            })
            .catch((error) => {
              console.error('Failed to load user info:', error);
            });
          
          // Setup token refresh
          setupTokenRefresh(kc);
        } else {
          console.warn('User not authenticated after init, redirecting to login...');
          setKeycloak(kc);
          kc.login();
        }
      })
      .catch((error) => {
        console.error('Keycloak initialization failed:', error);
        setAuthenticated(false);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      // Cleanup if needed
    };
  }, []); // Empty dependency array - run only once

  const setupTokenRefresh = (kc: Keycloak) => {
    // Refresh token every minute
    const interval = setInterval(() => {
      kc.updateToken(70)
        .then((refreshed) => {
          if (refreshed) {
            console.log('Token refreshed successfully');
          } else {
            console.log('Token still valid');
          }
        })
        .catch((error) => {
          console.error('Failed to refresh token:', error);
          clearInterval(interval);
          // Force re-login if refresh fails
          kc.login();
        });
    }, 60000); // Check every minute

    return interval;
  };

  const logout = () => {
    if (keycloak) {
      console.log('Logging out...');
      keycloak.logout({
        redirectUri: window.location.origin,
      });
    }
  };

  return {
    keycloak,
    authenticated,
    loading,
    userInfo,
    logout,
  };
};
